import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const EdgeFunctionLogsPanel = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFunction, setSelectedFunction] = useState('all');

  const edgeFunctions = [
    { value: 'all', label: 'All Functions' },
    { value: 'send-account-activation-email', label: 'Account Activation' },
    { value: 'send-billing-notification', label: 'Billing Notification' },
    { value: 'send-compliance-email', label: 'Compliance Alert' },
    { value: 'send-payment-reminder', label: 'Payment Reminder' }
  ];

  useEffect(() => {
    loadLogs();
  }, [selectedFunction]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      
      // Query notification_queue for edge function execution logs
      let query = supabase
        ?.from('notification_queue')
        ?.select('*')
        ?.order('created_at', { ascending: false })
        ?.limit(50);

      const { data, error } = await query;

      if (error) throw error;
      setLogs(data || []);
    } catch (error) {
      console.error('Error loading edge function logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getExecutionTime = (log) => {
    if (!log?.sent_at || !log?.created_at) return 'N/A';
    const diff = new Date(log?.sent_at)?.getTime() - new Date(log?.created_at)?.getTime();
    return `${diff}ms`;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-4">
        <Icon name="Terminal" size={24} className="text-blue-600 dark:text-blue-400" />
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Edge Function Execution Logs</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">Inspect edge function traces, performance metrics, and debugging information</p>
        </div>
      </div>

      {/* Function Filter */}
      <div className="flex items-center gap-4">
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Filter by Function:
        </label>
        <select
          value={selectedFunction}
          onChange={(e) => setSelectedFunction(e?.target?.value)}
          className="px-4 py-2 border border-neutral-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-700 dark:text-white"
        >
          {edgeFunctions?.map((func) => (
            <option key={func?.value} value={func?.value}>
              {func?.label}
            </option>
          ))}
        </select>
        <button
          onClick={loadLogs}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Icon name="RefreshCw" size={18} />
          Refresh
        </button>
      </div>

      {/* Logs Display */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Icon name="Loader" size={32} className="animate-spin text-blue-600" />
        </div>
      ) : (
        <div className="space-y-3">
          {logs?.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 dark:bg-slate-700 rounded-lg">
              <Icon name="FileText" size={48} className="mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600 dark:text-gray-400">No execution logs found</p>
            </div>
          ) : (
            logs?.map((log) => (
              <div
                key={log?.id}
                className="bg-white dark:bg-slate-800 rounded-lg border border-neutral-200 dark:border-slate-700 p-4"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${
                      log?.notification_status === 'delivered' ?'bg-green-100 text-green-600 dark:bg-green-900/20 dark:text-green-400'
                        : log?.notification_status === 'failed' ?'bg-red-100 text-red-600 dark:bg-red-900/20 dark:text-red-400' :'bg-blue-100 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400'
                    }`}>
                      <Icon name="Terminal" size={18} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">
                        {log?.notification_type?.replace(/_/g, ' ')?.toUpperCase() || 'Email Function'}
                      </p>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        {new Date(log?.created_at)?.toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    log?.notification_status === 'delivered' ?'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400'
                      : log?.notification_status === 'failed' ?'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400' :'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400'
                  }`}>
                    {log?.notification_status}
                  </span>
                </div>

                <div className="grid grid-cols-3 gap-4 mb-3">
                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Recipient</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {log?.recipient_email || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Execution Time</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {getExecutionTime(log)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Retry Count</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {log?.retry_count || 0}
                    </p>
                  </div>
                </div>

                {log?.error_message && (
                  <div className="bg-red-50 dark:bg-red-900/20 rounded p-3 border border-red-200 dark:border-red-800">
                    <p className="text-xs font-semibold text-red-900 dark:text-red-200 mb-1">Error Stack Trace</p>
                    <p className="text-xs text-red-700 dark:text-red-300 font-mono">
                      {log?.error_message}
                    </p>
                  </div>
                )}

                <details className="mt-3">
                  <summary className="text-xs cursor-pointer text-blue-600 dark:text-blue-400 hover:underline">
                    View Full Execution Details
                  </summary>
                  <pre className="mt-2 p-3 bg-gray-900 text-green-400 rounded text-xs overflow-x-auto">
                    {JSON.stringify(log, null, 2)}
                  </pre>
                </details>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default EdgeFunctionLogsPanel;